/**
 * Service API pour le module Caisse.
 * Passe par l'instance axios configurée dans api.ts (baseURL = /api).
 */
import api from './api';
import type {
  FolioCaisse,
  FolioCaisseDetail,
  BonConsultation,
  OperationCaisse,
  EncaisserBonData,
  CreateOperationData,
  FolioMobileMoney,
  FolioMobileMoneyDetail,
  OperationMobileMoney,
  CreateOperationMobileData,
} from '../types/caisse';

const BASE = '/caisse';

export const caisseApi = {
  // ---- Folios ----
  getFolios: () =>
    api.get<FolioCaisse[] | { results: FolioCaisse[] }>(`${BASE}/folios/`).then(r => r.data),

  // Folio du jour (ou d'une date donnée) avec ses opérations. Seul aujourd'hui est
  // créé d'office ; `creer` force la création d'un jour passé (id nul sinon).
  getFolioDuJour: (date?: string, creer = false) =>
    api.get<FolioCaisseDetail>(`${BASE}/folios/du_jour/`, {
      params: { ...(date ? { date } : {}), ...(creer ? { creer: 1 } : {}) },
    }).then(r => r.data),

  getFolio: (id: number) =>
    api.get<FolioCaisseDetail>(`${BASE}/folios/${id}/`).then(r => r.data),

  updateFolio: (id: number, data: Partial<FolioCaisse>) =>
    api.patch<FolioCaisse>(`${BASE}/folios/${id}/`, data).then(r => r.data),

  cloturerFolio: (id: number, notes?: string) =>
    api.post<FolioCaisseDetail>(`${BASE}/folios/${id}/cloturer/`, { notes }).then(r => r.data),

  // ---- Opérations ----
  createOperation: (data: CreateOperationData) =>
    api.post<OperationCaisse>(`${BASE}/operations/`, data).then(r => r.data),

  deleteOperation: (id: number) =>
    api.delete(`${BASE}/operations/${id}/`),

  // ---- Bons de consultation ----
  getBons: (params?: { statut?: string; patient?: number; search?: string }) =>
    api.get<BonConsultation[] | { results: BonConsultation[] }>(`${BASE}/bons/`, { params }).then(r => r.data),

  getBonsEnAttente: () =>
    api.get<BonConsultation[]>(`${BASE}/bons/en_attente/`).then(r => r.data),

  encaisserBon: (id: number, data?: EncaisserBonData) =>
    api.post<{
      message: string;
      facture_id: number;
      facture_numero: string;
      paiement_id: number;
      montant_encaisse: string;
      restant: string;
      bon: BonConsultation;
    }>(
      `${BASE}/bons/${id}/encaisser/`,
      data || {},
    ).then(r => r.data),

  annulerBon: (id: number, notes?: string) =>
    api.post<BonConsultation>(`${BASE}/bons/${id}/annuler/`, { notes }).then(r => r.data),

  // ---- Consultation -> caisse ----
  // Valide la fiche de consultation et génère le bon envoyé à la caisse
  envoyerConsultationCaisse: (consultationId: number) =>
    api.post<BonConsultation>(`/consultations/${consultationId}/envoyer_caisse/`).then(r => r.data),

  // ---- Folio Mobile Money ----
  getFolioMobileDuJour: (date?: string, creer = false) =>
    api.get<FolioMobileMoneyDetail>(`${BASE}/folios-mobile-money/du_jour/`, {
      params: { ...(date ? { date } : {}), ...(creer ? { creer: 1 } : {}) },
    }).then(r => r.data),

  cloturerFolioMobile: (id: number, notes?: string) =>
    api.post<FolioMobileMoneyDetail>(`${BASE}/folios-mobile-money/${id}/cloturer/`, { notes }).then(r => r.data),

  createOperationMobile: (data: CreateOperationMobileData) =>
    api.post<OperationMobileMoney>(`${BASE}/operations-mobile-money/`, data).then(r => r.data),

  deleteOperationMobile: (id: number) =>
    api.delete(`${BASE}/operations-mobile-money/${id}/`),
};

// Helper d'affichage des montants en GNF
export const normalizeList = <T,>(data: T[] | { results: T[] }): T[] =>
  Array.isArray(data) ? data : data.results;
